/**
 * 纯音频控制器 - 专门处理音频播放
 * 从 HybridAudioController 中提取音频相关功能，移除视频逻辑
 */
import musicCache from '@/utils/musicCache';
import * as tokenService from '@/utils/tokenService';
import { calculateBlobMD5, calculateFastMD5, generateMD5CacheKey, extractSongIdFromUrl, extractSongIdFromCacheKey, isCacheKeyBelongsToUser } from '@/utils/audioMD5';
import smartCacheManager from '@/services/SmartCacheManager.js';
import myRequest from '@/utils/myRequest';
import { isExternalPluginSource } from '@/music/plugins/externalPluginResolver.js';
import {
  canAnalyzeAudioUrl,
  getWebAudioTappedElement,
  resetWebAudioTapState,
  isWebAudioTappedElement,
  closeCoverParticleAudioContext,
} from '@/utils/coverParticleAudioBridge';
import { retypeAudioBlob } from '@/utils/audioFormatSniff';

// 全局复用的 audio 元素，避免在移动端失去用户手势上下文
let sharedAudioElement = null;

// 播放模式枚举
const PLAYBACK_MODES = {
  STREAMING: 'streaming',    // 流式播放模式
  DOWNLOADING: 'downloading', // 完整下载模式  
  CACHED: 'cached'           // 缓存播放模式
};

// 超时配置
const STREAMING_TIMEOUT = 12000;
const MIN_BUFFER_TIME = 5;
const SWITCH_CANCEL_TIMEOUT = 10000;

// 功能配置
const ENABLE_SMART_CACHE_SWITCH = true;

class AudioController {
  constructor() {
    // 绑定事件处理方法
    this.handleTimeUpdate = this.handleTimeUpdate.bind(this);
    this.handleEnded = this.handleEnded.bind(this);
    this.handlePlay = this.handlePlay.bind(this);
    this.handlePause = this.handlePause.bind(this);
    this.handleError = this.handleError.bind(this);
    this.handleLoadedMetadata = this.handleLoadedMetadata.bind(this);
    this.handleCanPlay = this.handleCanPlay.bind(this);
    this.handleProgress = this.handleProgress.bind(this);
    this.handleWaiting = this.handleWaiting.bind(this);
    this.handlePlaying = this.handlePlaying.bind(this);
    this.handleSeeked = this.handleSeeked.bind(this);
    this.handlePageUnload = this.handlePageUnload.bind(this);
    
    // 音频元素
    this.audio = null;
    this.audioListenersBound = false;
    this.currentMode = null;
    this.currentUserId = null;
    this.currentSongUrl = null;
    this.currentSongId = null;
    this.currentSong = null; // 🔥 新增：保存当前歌曲信息，用于缓存监听
    this.currentBlobUrl = null;
    this.currentAliyunDirectUrl = null;
    this.currentAudioMimeType = null;
    this.aliyunRecoverInProgress = false;
    this.aliyunRecoverAttempts = 0;
    this.aliyunSeekGraceUntil = 0;
    this.aliyunSeekErrorTimer = null;
    this.aliyunUserSeekActive = false;
    this.aliyunBufferErrorTimer = null;
    this.aliyunBufferRecoverTimer = null;
    /** 并发取链去重：songId:refresh → Promise */
    this._aliyunUrlInflight = new Map();
    /** 短缓存直链，避免同秒重复 HTTP */
    this._aliyunUrlCache = new Map();
    this._aliyunCanPlayNudgeTimer = null;
    this._aliyunCanPlayPollTimer = null;
    this._aliyunCanPlayEmittedForGeneration = -1;
    this.loadGeneration = 0;
    this.forceAliyunRefresh = false;
    this.aliyunSongLoadedAt = 0;
    this.proxySeekRecoverTimer = null;
    this.wasPlayingBeforeUserSeek = false;
    this.lastProxyStreamUrl = null;
    
    // 流式播放相关
    this.streamingTimeout = null;
    this.switchCancelTimeout = null;
    this.bufferMonitorInterval = null;
    this.retryCount = 0;
    this.maxRetries = 3;
    
    // 无感知切换相关状态
    this.isSwitching = false;
    this.savedCurrentTime = 0;
    this.savedVolume = 1;
    
    // 缓存策略相关状态
    this.playStartTime = null;
    this.cacheDelayTimer = null;
    this.shouldCacheAfterDelay = false;
    
    this.backgroundDownloadPromise = null;
    this.bufferCheckInterval = null;
    
    // 请求取消控制
    this.currentLoadAbortController = null;
    this.currentDownloadAbortController = null;
    this.backgroundDownloadAbortController = null;
    
    // 下载状态
    this.isDownloading = false;
    this.downloadProgress = 0;
    
    // 高频率时间更新相关状态
    this.isHighFreqTimeUpdateActive = false;
    this.highFreqTimeUpdateInterval = null;
    this.debugCounter = 0;
    
    // 事件回调集合
    this.eventCallbacks = {
      timeupdate: [],
      ended: [],
      play: [],
      pause: [],
      error: [],
      loadedmetadata: [],
      canplay: [],
      downloadprogress: [],
      progress: [],
      waiting: [],
      playing: []
    };
    
    // 绑定页面卸载事件
    window.addEventListener('beforeunload', this.handlePageUnload);
  }

  // 创建或复用音频元素
  createAudioElement() {
    if (this.audio) {
      console.log('🎵 [AudioController] 复用已存在的音频元素，重置状态');
      this.resetExistingAudioElement();
      return;
    }
    
    if (sharedAudioElement) {
      console.log('🎵 [AudioController] 复用全局音频元素');
      this.audio = sharedAudioElement;
      this.resetExistingAudioElement();
      this.bindAudioEventListeners();
      return;
    }
    
    this.audio = new Audio();
    // 🔥 性能优化：使用 'metadata' 而非 'auto'，让浏览器只加载元数据即可触发 canplay，
    // 实现边下边播（progressive streaming），避免对阿里云盘等远端直链一次性缓冲整个文件
    // 导致首播延迟（实测 9MB 音频在 'auto' 下需等待 ~25s 下载完才开始播放）。
    this.audio.preload = 'metadata';
    this.audio.crossOrigin = 'anonymous';
    sharedAudioElement = this.audio;
    
    this.bindAudioEventListeners();
    
    console.log('🎵 [AudioController] 音频元素创建完成');
  }

  bindAudioEventListeners() {
    if (!this.audio || this.audioListenersBound) {
      return;
    }
    this.audio.addEventListener('timeupdate', this.handleTimeUpdate);
    this.audio.addEventListener('ended', this.handleEnded);
    this.audio.addEventListener('play', this.handlePlay);
    this.audio.addEventListener('pause', this.handlePause);
    this.audio.addEventListener('error', this.handleError);
    this.audio.addEventListener('loadedmetadata', this.handleLoadedMetadata);
    this.audio.addEventListener('canplay', this.handleCanPlay);
    this.audio.addEventListener('progress', this.handleProgress);
    this.audio.addEventListener('waiting', this.handleWaiting);
    this.audio.addEventListener('playing', this.handlePlaying);
    this.audio.addEventListener('seeked', this.handleSeeked);
    this.audioListenersBound = true;
  }

  removeAudioEventListeners() {
    if (!this.audio || !this.audioListenersBound) {
      return;
    }
    try {
      this.audio.removeEventListener('timeupdate', this.handleTimeUpdate);
      this.audio.removeEventListener('ended', this.handleEnded);
      this.audio.removeEventListener('play', this.handlePlay);
      this.audio.removeEventListener('pause', this.handlePause);
      this.audio.removeEventListener('error', this.handleError);
      this.audio.removeEventListener('loadedmetadata', this.handleLoadedMetadata);
      this.audio.removeEventListener('canplay', this.handleCanPlay);
      this.audio.removeEventListener('progress', this.handleProgress);
      this.audio.removeEventListener('waiting', this.handleWaiting);
      this.audio.removeEventListener('playing', this.handlePlaying);
      this.audio.removeEventListener('seeked', this.handleSeeked);
    } catch (error) {
      console.warn('🧹 [AudioController] 移除音频事件监听器时出错:', error);
    }
    this.audioListenersBound = false;
  }

  resetExistingAudioElement() {
    if (!this.audio) {
      return;
    }
    try {
      this.audio.pause();
      this.audio.currentTime = 0;
    } catch (error) {
      console.warn('🎵 [AudioController] 重置音频元素时出错:', error);
    }
    // 清空 src，调用 load() 让浏览器停止对旧资源的拉取
    this.audio.src = '';
    this.audio.load();
  }

  /**
   * 为下一首歌准备音频控制器（保留 audio 元素，重置内部状态）
   */
  prepareForNextSong() {
    console.log('🎵 [AudioController] 准备加载新歌曲，复用音频元素');

    const wasAliyunDirect = !!this.currentAliyunDirectUrl;
    this.clearAliyunCanPlayNudge();
    
    if (this.streamingTimeout) {
      clearTimeout(this.streamingTimeout);
      this.streamingTimeout = null;
    }
    
    if (this.switchCancelTimeout) {
      clearTimeout(this.switchCancelTimeout);
      this.switchCancelTimeout = null;
    }
    
    if (this.bufferMonitorInterval) {
      clearInterval(this.bufferMonitorInterval);
      this.bufferMonitorInterval = null;
    }
    
    if (this.cacheDelayTimer) {
      clearTimeout(this.cacheDelayTimer);
      this.cacheDelayTimer = null;
    }
    
    if (this.bufferCheckInterval) {
      clearInterval(this.bufferCheckInterval);
      this.bufferCheckInterval = null;
    }
    
    this.stopHighFreqTimeUpdate();
    
    if (this.audio) {
      try {
        this.audio.pause();
        this.audio.currentTime = 0;
        this.audio.removeAttribute('src');
        while (this.audio.firstChild) {
          this.audio.removeChild(this.audio.firstChild);
        }
        this.audio.load();
      } catch (error) {
        console.warn('🎵 [AudioController] 重置音频元素时出错:', error);
      }
    }
    
    if (this.currentBlobUrl) {
      try {
        URL.revokeObjectURL(this.currentBlobUrl);
      } catch (error) {
        console.warn('🎵 [AudioController] 清理Blob URL时出错:', error);
      }
      this.currentBlobUrl = null;
    }
    
    this.stopSmartCacheMonitoring();
    
    this.currentMode = null;
    this.currentSongUrl = null;
    this.currentSongId = null;
    this.currentSong = null;
    this.currentAliyunDirectUrl = null;
    this.currentAudioMimeType = null;
    this.aliyunRecoverAttempts = 0;
    this.aliyunRecoverInProgress = false;
    this.forceAliyunRefresh = wasAliyunDirect;
    this.isSwitching = false;
    this.shouldCacheAfterDelay = false;
    this.loadGeneration = (this.loadGeneration || 0) + 1;

    if (this.aliyunBufferErrorTimer) {
      clearTimeout(this.aliyunBufferErrorTimer);
      this.aliyunBufferErrorTimer = null;
    }
    if (this.aliyunBufferRecoverTimer) {
      clearTimeout(this.aliyunBufferRecoverTimer);
      this.aliyunBufferRecoverTimer = null;
    }
    if (this.aliyunSeekErrorTimer) {
      clearTimeout(this.aliyunSeekErrorTimer);
      this.aliyunSeekErrorTimer = null;
    }
    this.clearProxySeekRecoverTimer();
    this.wasPlayingBeforeUserSeek = false;
    this.lastProxyStreamUrl = null;
    this.aliyunUserSeekActive = false;
    this.aliyunSeekGraceUntil = 0;
  }

  /**
   * 跨域 CDN 音频被 Web Audio 接入后会永久无声，需重建 audio 元素
   */
  ensureUntappedAudioElementForUrl(targetUrl) {
    if (!this.audio) return;
    if (canAnalyzeAudioUrl(targetUrl)) return;
    if (getWebAudioTappedElement() !== this.audio) return;

    console.warn('🎵 [AudioController] 跨域音频不可 Web Audio 分析，重建 audio 元素以恢复出声');
    this.removeAudioEventListeners();
    try {
      this.audio.pause();
    } catch {
      /* ignore */
    }
    if (sharedAudioElement === this.audio) {
      sharedAudioElement = null;
    }
    resetWebAudioTapState();
    this.audio = null;
    this.audioListenersBound = false;
    this.createAudioElement();
  }

  /**
   * iOS 切后台会挂起 AudioContext：粒子分析一旦 createMediaElementSource，
   * 出声就绑在 Context 上 → 后台静音，回前台 resume 还可能短暂降调。
   * 切后台时重建未 tap 的 audio，改回 HTMLAudio 原生出声以保持后台播放。
   * @returns {boolean} 是否执行了重建
   */
  releaseWebAudioTapKeepingPlayback() {
    if (!this.audio || !isWebAudioTappedElement(this.audio)) {
      return false;
    }

    const oldAudio = this.audio;
    const src = oldAudio.currentSrc || oldAudio.src;
    if (!src) return false;

    const wasPlaying = !oldAudio.paused && !oldAudio.ended;
    const currentTime = Number.isFinite(oldAudio.currentTime) ? oldAudio.currentTime : 0;
    const volume = oldAudio.volume;
    const muted = oldAudio.muted;
    const playbackRate = oldAudio.playbackRate || 1;
    const mimeType = this.currentAudioMimeType;

    console.warn('🎵 [AudioController] 切后台释放 Web Audio tap，重建原生出声以保持后台播放', {
      wasPlaying,
      currentTime: Math.round(currentTime * 10) / 10,
    });

    this.removeAudioEventListeners();
    try {
      oldAudio.pause();
    } catch {
      /* ignore */
    }
    if (sharedAudioElement === oldAudio) {
      sharedAudioElement = null;
    }

    void closeCoverParticleAudioContext();
    this.audio = null;
    this.audioListenersBound = false;
    this.createAudioElement();

    if (!this.audio) return false;

    try {
      this.audio.volume = volume;
      this.audio.muted = muted;
      this.audio.playbackRate = playbackRate;
      if (mimeType && typeof MediaSource !== 'undefined') {
        // 部分浏览器对 type hint 敏感；能设则设
        try {
          this.audio.setAttribute('type', mimeType);
        } catch {
          /* ignore */
        }
      }
      this.audio.src = src;
      if (currentTime > 0.25) {
        const seekTo = currentTime;
        const onMeta = () => {
          try {
            if (Math.abs(this.audio.currentTime - seekTo) > 0.35) {
              this.audio.currentTime = seekTo;
            }
          } catch {
            /* ignore */
          }
          this.audio.removeEventListener('loadedmetadata', onMeta);
        };
        this.audio.addEventListener('loadedmetadata', onMeta);
        try {
          this.audio.currentTime = seekTo;
        } catch {
          /* wait metadata */
        }
      }
      if (wasPlaying) {
        this.audio.play().catch((error) => {
          console.warn('🎵 [AudioController] 释放 Web Audio 后恢复播放失败:', error);
        });
      }
    } catch (error) {
      console.warn('🎵 [AudioController] 释放 Web Audio tap 失败:', error);
      return false;
    }

    return true;
  }

  /**
   * 加载歌曲
   * @param {string|number|Object} urlOrSongIdOrSong - 音频URL、歌曲ID或歌曲对象（推荐传递歌曲对象）
   * @param {number|Object} startTimeOrOptions - 开始时间（秒）或选项对象（如果第一个参数是歌曲对象，此参数也可以是包含storageType的对象）
   * @returns {Promise<boolean>}
   */
  async loadSong(urlOrSongIdOrSong, startTimeOrOptions = 0) {
    console.log('🎵 [AudioController] ===== loadSong 方法开始 =====');
    
    try {
      this.prepareForNextSong();
      // 解析参数
      let startTime = 0;
      let audioStorageType = null; // 音频存储类型：0-本地存储，1-OneDrive存储
      
      // 🔥 支持传递歌曲对象
      let song = null;
      if (typeof urlOrSongIdOrSong === 'object' && urlOrSongIdOrSong !== null && urlOrSongIdOrSong.id) {
        // 传递的是歌曲对象
        song = urlOrSongIdOrSong;
        this.currentSong = song; // 🔥 保存当前歌曲信息，用于缓存监听
        audioStorageType = song.audio_storage_type || song.audioStorageType;
        
        if (typeof startTimeOrOptions === 'number') {
          startTime = startTimeOrOptions;
        } else if (typeof startTimeOrOptions === 'object') {
          startTime = startTimeOrOptions.startTime || 0;
          // 如果选项中有 storageType，优先使用
          audioStorageType = audioStorageType || startTimeOrOptions.storageType || startTimeOrOptions.audio_storage_type;
        }
      } else {
        // 传递的是 URL 或 songId
        startTime = typeof startTimeOrOptions === 'number' ? startTimeOrOptions : (startTimeOrOptions.startTime || 0);
        // 尝试从选项中获取 storageType
        if (typeof startTimeOrOptions === 'object') {
          audioStorageType = startTimeOrOptions.storageType || startTimeOrOptions.audio_storage_type;
        }
      }

      // 🔥 关键修复：storageType 可能是字符串（如 "2"），统一归一化为数字，
      // 否则后续 `=== 2` / `=== 1` / `=== 0` 的严格比较会失败，导致阿里云盘音频
      // 误入"未知"分支走后端 302 流式接口，造成 ~20s 的加载延迟。
      if (audioStorageType !== null && audioStorageType !== undefined && audioStorageType !== '') {
        const parsedType = Number(audioStorageType);
        audioStorageType = Number.isNaN(parsedType) ? audioStorageType : parsedType;
      }
      
      // 🚀 关键修复：对于在线搜索的歌曲，直接使用搜索API返回的音频URL，不查询数据库
      // 🔥 新增：对于分享音乐，如果已有streamUrl（OneDrive直链），直接使用，不尝试重新获取
      this.createAudioElement();
      
      if (song) {
        console.log('🔍 [AudioController] 检查歌曲属性:', {
          title: song.title,
          id: song.id,
          isOnline: song.isOnline,
          isExternalWithResources: song.isExternalWithResources,
          isShareMusic: song.isShareMusic,
          playUrl: song.playUrl,
          streamUrl: song.streamUrl,
          url: song.url
        });
        
        // 分享音乐：优先使用 CDN 直链；否则走分享专用 stream（勿回落到 /api/music/{id}/stream）
        if (song.isShareMusic) {
          const audioUrl = song.streamUrl || song.url || song.playUrl;
          if (audioUrl) {
            const isCdnUrl = audioUrl.startsWith('http://') || audioUrl.startsWith('https://');
            const isShareStream = !isCdnUrl && audioUrl.includes('/api/music/share/') && audioUrl.includes('/stream');
            if (isCdnUrl || isShareStream) {
              console.log('🔗 [AudioController] 分享音乐，使用', isCdnUrl ? 'CDN直链' : '分享流式接口', ':', song.title);
              console.log('🔗 [AudioController] URL:', audioUrl.substring(0, 80) + '...');
              this.currentSongId = String(song.id);
              return await this.playFromCDN(audioUrl, startTime);
            }
            console.log('🔗 [AudioController] 检测到分享音乐，但URL格式未识别:', audioUrl);
          } else {
            console.warn('⚠️ [AudioController] 分享音乐但没有找到音频URL:', song.title);
          }
        }
        
        if (song.isOnline || song.isExternalWithResources) {
          const audioUrl = song.playUrl || song.streamUrl || song.url;
          if (audioUrl) {
            console.log('🌐 [AudioController] 检测到在线搜索歌曲，直接使用搜索API返回的音频URL:', song.title);
            console.log('🌐 [AudioController] 音频URL:', audioUrl);
            console.log('🌐 [AudioController] 跳过数据库查询，直接播放');
            // 直接使用搜索API返回的URL播放
            return await this.playFromCDN(audioUrl, startTime);
          }

          if (isExternalPluginSource(song.source) || song.isExternalWithResources) {
            throw new Error(`外部音源歌曲缺少播放地址: ${song.title || song.id}`);
          }

          console.warn('⚠️ [AudioController] 在线搜索歌曲但没有找到音频URL:', song.title);
        }
      }
      
      // 获取用户ID和歌曲ID
      const userId = tokenService.getCurrentUserId();
      
      // 🔥 优化：支持直接传递 songId（数字或字符串），也兼容 URL 格式
      let songId;
      let fallbackUrl = null; // 用于流式播放的回退 URL
      
      if (song) {
        // 从歌曲对象中获取信息
        songId = String(song.id);
        console.log('🎵 [AudioController] 从歌曲对象获取ID:', songId);
        console.log('🎵 [AudioController] 音频存储类型:', audioStorageType, audioStorageType === 1 ? '(OneDrive)' : audioStorageType === 0 ? '(本地)' : audioStorageType === 2 ? '(阿里云盘)' : '(未知)');
        
        // 构造回退 URL
        const baseUrl = import.meta.env.VITE_APP_AUDIO_STREAM_BASE_URL || '';
        fallbackUrl = baseUrl 
          ? `${baseUrl}/api/music/${songId}/stream`
          : `/api/music/${songId}/stream`;
      } else if (typeof urlOrSongIdOrSong === 'number' || (typeof urlOrSongIdOrSong === 'string' && /^\d+$/.test(urlOrSongIdOrSong))) {
        // 直接传递的是 songId
        songId = String(urlOrSongIdOrSong);
        console.log('🎵 [AudioController] 直接使用歌曲ID:', songId);
        // 构造回退 URL
        const baseUrl = import.meta.env.VITE_APP_AUDIO_STREAM_BASE_URL || '';
        fallbackUrl = baseUrl 
          ? `${baseUrl}/api/music/${songId}/stream`
          : `/api/music/${songId}/stream`;
      } else {
        // 传递的是 URL，需要从 URL 中提取 songId
        songId = extractSongIdFromUrl(urlOrSongIdOrSong);
        fallbackUrl = urlOrSongIdOrSong; // 使用传入的 URL 作为回退
        console.log('🎵 [AudioController] 从URL提取歌曲ID:', urlOrSongIdOrSong, '->', songId);
      }
      
      if (!songId) {
        // 如果无法提取 songId，尝试从歌曲对象中获取
        if (song && song.id) {
          songId = String(song.id);
          console.log('🎵 [AudioController] 从歌曲对象中获取ID作为备用:', songId);
        } else {
          // 如果确实没有 songId，直接使用 URL 播放（外部音乐等）
        console.log('🎵 [AudioController] 无法提取歌曲ID，直接使用URL播放:', urlOrSongIdOrSong);
          // 🔥 修复：即使没有songId，也要尝试设置currentSongId（如果歌曲对象中有id）
          // 这样智能缓存监听才能正常工作
          if (song && song.id) {
            this.currentSongId = String(song.id);
            console.log('🎵 [AudioController] 设置currentSongId（从歌曲对象）:', this.currentSongId);
          }
        return await this.playFromCDN(urlOrSongIdOrSong, startTime);
        }
      }
      
      console.log('🎵 [AudioController] 用户ID:', userId);
      console.log('🎵 [AudioController] 歌曲ID:', songId);
      
      // 🔥 保存当前歌曲ID，用于缓存监听
      this.currentSongId = songId;
      
      // 🔥 关键修复：优先检查缓存，确保缓存优先于 OneDrive 播放
      // 1. 检查现有缓存（使用 userId + songId）
      console.log('🎵💾 [AudioController] ===== 开始检查缓存 =====');
      console.log('🎵💾 [AudioController] 正在检查现有缓存，用户ID:', userId, '歌曲ID:', songId);
      const existingCacheKey = await this.findExistingCache(userId, songId);
      if (existingCacheKey) {
        console.log(`🎵✅ [AudioController] 找到现有缓存: ${existingCacheKey}`);
        console.log('🎵💾 [AudioController] 优先使用缓存播放，跳过 OneDrive 和流式接口');
        return await this.playFromCache(existingCacheKey, startTime, { expectedDuration: song?.duration || 0, fallbackUrl, userId, songId });
      } else {
        console.log('🎵💾 [AudioController] 未找到现有缓存');
      }
      
      // 2. 检查并迁移旧格式缓存
      console.log('🎵💾 [AudioController] 正在检查并迁移旧格式缓存');
      const migratedCacheKey = await this.migrateOldCaches(userId, songId, fallbackUrl);
      if (migratedCacheKey) {
        console.log(`🎵✅ [AudioController] 旧缓存迁移完成: ${migratedCacheKey}`);
        console.log('🎵💾 [AudioController] 使用迁移的缓存播放，跳过 OneDrive 和流式接口');
        return await this.playFromCache(migratedCacheKey, startTime, { expectedDuration: song?.duration || 0, fallbackUrl, userId, songId });
      } else {
        console.log('🎵💾 [AudioController] 未找到可迁移的旧缓存');
      }
      
      console.log('🎵💾 [AudioController] ===== 缓存检查完成，未找到 music-files 整文件缓存 =====');
      
      // 3. 🔥 优化：根据 audio_storage_type 决定加载策略
      // audio_storage_type = 1 (OneDrive)：直接尝试获取 OneDrive 直链，跳过本地流式接口
      // audio_storage_type = 0 (本地)：直接使用本地流式接口，跳过 OneDrive 检查
      // audio_storage_type = null/undefined：回退到原有逻辑（先试 OneDrive，再试本地）
      
      if (audioStorageType === 1) {
        // OneDrive 存储：直接获取 OneDrive 直链
        console.log('🎵 [AudioController] 检测到 OneDrive 存储，直接获取 OneDrive 直链');
        const oneDriveUrl = await this.getOneDriveAudioUrl(songId);
        if (oneDriveUrl) {
          console.log('🎵 [AudioController] 使用 OneDrive 直链:', oneDriveUrl);
          return await this.playFromCDN(oneDriveUrl, startTime);
        } else {
          // OneDrive 获取失败，仍然回退到流式接口
          console.warn('🎵 [AudioController] OneDrive 直链获取失败，回退到流式接口');
          return await this.playFromCDN(fallbackUrl, startTime);
        }
      } else if (audioStorageType === 2) {
        // 🔥 阿里云盘存储：直接获取阿里云盘直链，由浏览器直连 CDN 取流，
        // 避免走本地 /stream 接口的后端 302 绕行（实测会导致 ~20s 延迟）。
        console.log('🎵 [AudioController] 检测到阿里云盘存储，直接获取阿里云盘直链');
        const refreshOnSwitch = this.forceAliyunRefresh;
        this.forceAliyunRefresh = false;
        const aliyunInfo = await this.getAliyunAudioUrl(songId, refreshOnSwitch);
        if (aliyunInfo?.url) {
          console.log('🎵 [AudioController] 使用阿里云盘直链:', aliyunInfo.url, aliyunInfo.mimeType || '');
          try {
            return await this.playFromCDN(aliyunInfo.url, startTime, { mimeType: aliyunInfo.mimeType });
          } catch (cdnError) {
            // 阿里云盘音频直链可能被 OSS 403（含"非分段下载 3 次/60 秒"限流）拒绝。
            // 错峰延迟后强制刷新换一条全新直链再试一次；仍失败则回退流式接口。
            console.warn('🎵 [AudioController] 阿里云盘音频直连失败，错峰刷新直链后重试:', cdnError?.message || cdnError);
            await new Promise(resolve => setTimeout(resolve, 1200 + Math.floor(Math.random() * 800)));
            const freshInfo = await this.getAliyunAudioUrl(songId, true);
            if (freshInfo?.url) {
              try {
                return await this.playFromCDN(freshInfo.url, startTime, { mimeType: freshInfo.mimeType });
              } catch (retryError) {
                console.warn('🎵 [AudioController] 阿里云盘音频刷新后仍失败，回退到流式接口:', retryError?.message || retryError);
              }
            }
            return await this.playFromCDN(fallbackUrl, startTime);
          }
        } else {
          // 获取失败，回退到流式接口（后端仍会 302 到直链）
          console.warn('🎵 [AudioController] 阿里云盘直链获取失败，回退到流式接口');
          return await this.playFromCDN(fallbackUrl, startTime);
        }
      } else if (audioStorageType === 0) {
        // 本地存储：直接使用流式接口，跳过 OneDrive 检查
        console.log('🎵 [AudioController] 检测到本地存储，直接使用流式接口，跳过 OneDrive 检查');
        return await this.playFromCDN(fallbackUrl, startTime);
      } else {
        // 未知存储类型：使用原有逻辑（先试 OneDrive，再试本地）
        console.log('🎵 [AudioController] 存储类型未知，使用原有逻辑：先试 OneDrive，再试本地');
        const oneDriveUrl = await this.getOneDriveAudioUrl(songId);
        if (oneDriveUrl) {
          console.log('🎵 [AudioController] 使用 OneDrive 直链:', oneDriveUrl);
          return await this.playFromCDN(oneDriveUrl, startTime);
        }
        
        // 最后回退到流式接口
        console.log('🎵 [AudioController] OneDrive 获取失败，回退到流式接口');
        return await this.playFromCDN(fallbackUrl, startTime);
      }
      
    } catch (error) {
      console.error('🎵 [AudioController] loadSong 失败:', error);
      this.emit('error', { error: error.message });
      throw error;
    }
  }
  
  /**
   * 获取 OneDrive 音频 URL
   * @param {string} songId - 歌曲ID
   * @returns {Promise<string|null>} OneDrive 直链 URL，如果不可用返回 null
   */
  async getOneDriveAudioUrl(songId) {
    try {
      console.log('🎵 [AudioController] 检查 OneDrive 状态...');
      
      // 1. 检查 OneDrive 状态
      const oneDriveStatus = await myRequest.get('/api/onedrive/status');
      console.log('🎵 [AudioController] OneDrive 状态检查结果:', oneDriveStatus);
      
      if (oneDriveStatus === true) {
        console.log('🎵 [AudioController] OneDrive 已连接，尝试获取 OneDrive 音频 URL...');
        
        // 2. 获取 OneDrive 直接 URL
        const oneDriveResponse = await myRequest.get(`/api/music/onedrive-direct-url/${songId}`);
        console.log('🎵 [AudioController] OneDrive 音频资源请求结果:', oneDriveResponse);
        
        if (oneDriveResponse && oneDriveResponse.directUrl) {
          console.log('🎵 [AudioController] 获取到 OneDrive 音频 URL:', oneDriveResponse.directUrl);
          return oneDriveResponse.directUrl;
        } else {
          console.warn('🎵 [AudioController] OneDrive 音频资源请求失败，没有返回 directUrl');
        }
      } else {
        console.log('🎵 [AudioController] OneDrive 未连接或状态异常:', oneDriveStatus);
      }
      
      return null;
    } catch (error) {
      console.warn('🎵 [AudioController] OneDrive 检查失败:', error);
      return null;
    }
  }

  /**
   * 获取阿里云盘音频直链
   * @param {string} songId - 歌曲ID
   * @param {boolean} [refresh=false]
   * @returns {Promise<{url: string, mimeType?: string}|null>}
   */
  async getAliyunAudioUrl(songId, refresh = false) {
    const cacheKey = String(songId);
    if (refresh) {
      this._aliyunUrlCache.delete(cacheKey);
    } else {
      const cached = this._aliyunUrlCache.get(cacheKey);
      if (cached && cached.expireAt > Date.now() && cached.url) {
        this.currentAudioMimeType = cached.mimeType || null;
        return { url: cached.url, mimeType: cached.mimeType || null };
      }
      const inflightKey = `${cacheKey}:0`;
      if (this._aliyunUrlInflight.has(inflightKey)) {
        return this._aliyunUrlInflight.get(inflightKey);
      }
    }

    const inflightKey = `${cacheKey}:${refresh ? '1' : '0'}`;
    const requestPromise = this.fetchAliyunAudioUrlOnce(songId, refresh)
      .then((result) => {
        if (result?.url) {
          this._aliyunUrlCache.set(cacheKey, {
            url: result.url,
            mimeType: result.mimeType || null,
            // OSS 直链通常十几分钟有效；前端短缓存只为合并同秒重复请求
            expireAt: Date.now() + 45 * 1000
          });
        }
        return result;
      })
      .finally(() => {
        if (this._aliyunUrlInflight.get(inflightKey) === requestPromise) {
          this._aliyunUrlInflight.delete(inflightKey);
        }
      });

    this._aliyunUrlInflight.set(inflightKey, requestPromise);
    return requestPromise;
  }

  async fetchAliyunAudioUrlOnce(songId, refresh = false) {
    try {
      console.log('🎵 [AudioController] 获取阿里云盘音频直链, songId:', songId, 'refresh:', refresh);
      const query = refresh ? '?refresh=true' : '';
      const resp = await myRequest.get(`/api/aliyun/media/download-url/${songId}${query}`);
      if (resp && resp.url) {
        console.log('🎵 [AudioController] 获取到阿里云盘音频直链', {
          mimeType: resp.mimeType || null,
          declaredMimeType: resp.declaredMimeType || null
        });
        this.currentAudioMimeType = resp.mimeType || null;
        return {
          url: resp.url,
          mimeType: resp.mimeType || null
        };
      }
      console.warn('🎵 [AudioController] 阿里云盘直链响应无 url 字段:', resp);
      return null;
    } catch (error) {
      console.warn('🎵 [AudioController] 获取阿里云盘音频直链失败:', error);
      return null;
    }
  }

  /** M4A/AAC 在 iOS + preload=metadata 时 canplay 很慢，需更积极缓冲 */
  isSlowStartAliyunMime(mimeType) {
    if (!mimeType || typeof mimeType !== 'string') return false;
    const m = mimeType.toLowerCase();
    return m.includes('mp4') || m.includes('m4a') || m.includes('aac');
  }

  clearAliyunCanPlayNudge() {
    if (this._aliyunCanPlayNudgeTimer) {
      clearTimeout(this._aliyunCanPlayNudgeTimer);
      this._aliyunCanPlayNudgeTimer = null;
    }
    if (this._aliyunCanPlayPollTimer) {
      clearInterval(this._aliyunCanPlayPollTimer);
      this._aliyunCanPlayPollTimer = null;
    }
  }

  /**
   * 阿里云直链：元数据就绪后加速缓冲，并在 readyState 足够时提前发出 canplay，
   * 避免 iOS 对 M4A 卡在 metadata→canplay 之间约 7 秒。
   */
  scheduleAliyunCanPlayNudge(loadGeneration) {
    this.clearAliyunCanPlayNudge();
    if (!this.audio || !this.currentAliyunDirectUrl) return;

    if (this.isSlowStartAliyunMime(this.currentAudioMimeType)) {
      try {
        this.audio.preload = 'auto';
      } catch (_) {
        /* ignore */
      }
    }

    const emitEarly = (reason) => {
      if (this.loadGeneration !== loadGeneration) return false;
      if (this._aliyunCanPlayEmittedForGeneration === loadGeneration) return true;
      const readyState = this.audio?.readyState ?? 0;
      // HAVE_CURRENT_DATA=2 起即可尝试开声；deadline 时也允许
      if (readyState < 2 && reason !== 'deadline') return false;
      this._aliyunCanPlayEmittedForGeneration = loadGeneration;
      console.log('🎵 [AudioController] 阿里云直链提前 canplay:', { reason, readyState });
      this.emit('canplay');
      this.clearAliyunCanPlayNudge();
      return true;
    };

    // 立刻试一次（部分机型 metadata 时已有 CURRENT_DATA）
    if (emitEarly('immediate')) return;

    this._aliyunCanPlayPollTimer = setInterval(() => {
      emitEarly('readyState-poll');
    }, 150);

    // M4A：最多再等 ~1s；仍未 canplay 则强制开声（继续边下边播）
    const deadlineMs = this.isSlowStartAliyunMime(this.currentAudioMimeType) ? 1000 : 2500;
    this._aliyunCanPlayNudgeTimer = setTimeout(() => {
      emitEarly('deadline');
      this.clearAliyunCanPlayNudge();
    }, deadlineMs);
  }

  /**
   * 设置 audio 源；有明确 MIME 时用 <source type> 给 Safari 类型提示。
   */
  applyAudioSource(url, mimeType = null) {
    if (!this.audio) return;
    const typed = mimeType && mimeType !== 'application/octet-stream' ? mimeType : null;
    this.audio.removeAttribute('src');
    while (this.audio.firstChild) {
      this.audio.removeChild(this.audio.firstChild);
    }
    if (typed) {
      const source = document.createElement('source');
      source.src = url;
      source.type = typed;
      this.audio.appendChild(source);
      this.audio.load();
    } else {
      this.audio.src = url;
    }
  }

  // 从缓存播放
  async playFromCache(cacheKey, startTime = 0, options = {}) {
    console.log('🎵 [AudioController] 从缓存播放:', cacheKey);
    const { expectedDuration, fallbackUrl, userId, songId } = options || {};
    
    try {
      const cacheData = await musicCache.get(cacheKey);
      if (!cacheData) {
        throw new Error('缓存数据不存在');
      }
      
      // 创建Blob URL（按文件头纠正 MIME，避免假 MP3 缓存写成 audio/mpeg）
      const typedBlob = await retypeAudioBlob(cacheData, 'audio/mpeg');
      const blob = typedBlob;
      const blobUrl = URL.createObjectURL(blob);
      this.currentAudioMimeType = blob.type || null;

      // 先做一次粗略的体积校验（过小当作坏缓存）
      const MIN_BLOB_BYTES = 16 * 1024; // 16KB阈值，避免把极小文件当作完整音频
      if (blob.size && blob.size < MIN_BLOB_BYTES) {
        console.warn('⚠️ [AudioController] 缓存体积异常偏小，判定为坏缓存，删除并回退:', { size: blob.size, cacheKey });
        try { await musicCache.delete(cacheKey); } catch (e) {}
        URL.revokeObjectURL(blobUrl);
        // 回退到网络播放
        if (fallbackUrl) {
          return await this.playFromCDN(fallbackUrl, startTime);
        }
        return false;
      }
      
      // 设置音频源
      this.ensureUntappedAudioElementForUrl(blobUrl);
      this.applyAudioSource(blobUrl, blob.type || null);
      this.currentBlobUrl = blobUrl;
      this.currentMode = PLAYBACK_MODES.CACHED;
      
      // 等待加载完成并进行时长校验
      await new Promise((resolve, reject) => {
        this.audio.onloadedmetadata = () => {
          // 时长校验：如果明显短于期望，判为坏缓存
          const actual = this.audio.duration;
          const expected = Number(expectedDuration) || 0;
          let isInvalid = false;
          if (!isFinite(actual) || actual <= 0) {
            isInvalid = true;
          } else if (expected > 0) {
            const threshold = Math.min(Math.max(expected - 10, 0), expected * 0.6);
            if (actual < threshold) {
              isInvalid = true;
            }
          }
          if (isInvalid) {
            console.warn('⚠️ [AudioController] 检测到缓存时长异常，判定为坏缓存，删除并回退:', { actual, expected, cacheKey });
            try { this.audio.pause(); } catch {}
            try { this.audio.src = ''; } catch {}
            try { URL.revokeObjectURL(blobUrl); } catch {}
            try { this.currentBlobUrl = null; } catch {}
            // 删除坏缓存
            musicCache.delete(cacheKey).catch(() => {});
            // 回退到网络播放
            if (fallbackUrl) {
              // 注意：此时不再 resolve，直接切换到 CDN 播放
              this.playFromCDN(fallbackUrl, startTime)
                .then(resolve)
                .catch(reject);
              return;
            }
          }
          this.audio.currentTime = startTime;
          resolve();
        };
        this.audio.onerror = reject;
      });
      
      console.log('🎵 [AudioController] 缓存播放准备完成');
      return true;
      
    } catch (error) {
      console.error('🎵 [AudioController] 缓存播放失败:', error);
      // 缓存播放失败时，尝试回退
      if (fallbackUrl) {
        try {
          return await this.playFromCDN(fallbackUrl, startTime);
        } catch (e) {
          // 继续抛出
        }
      }
      throw error;
    }
  }

  /**
   * 判断是否为外部音乐服务 URL（如酷我、QQ音乐等）
   * @param {string} url - URL 字符串
   * @returns {boolean}
   */
  isExternalMusicUrl(url) {
    if (!url) return false;
    const externalDomains = [
      'kuwo.cn',
      'qq.com',
      'kugou.com',
      '126.net',
      '163.com',
      'baidu.com',
      'xiami.com',
      'douyinstatic.com',
      'cenguigui.cn',
      'migu.cn',
      // 洛雪音乐「长青SVIP音源」解析后端：部分线路（如酷我）不走重定向，
      // 直接返回音频二进制流，请求地址本身即为播放地址，需走后端代理避免CORS/Referer限制
      'haitangw.cc',
      'haitangw.net'
    ];
    return externalDomains.some(domain => url.includes(domain));
  }

  getInternalServiceOrigins() {
    const origins = new Set([window.location.origin]);
    for (const envKey of ['VITE_APP_API_BASE_URL', 'VITE_APP_AUDIO_STREAM_BASE_URL']) {
      const raw = import.meta.env[envKey];
      if (!raw) continue;
      try {
        origins.add(new URL(raw, window.location.origin).origin);
      } catch {
        // ignore invalid env URL
      }
    }
    return origins;
  }

  isSameOriginServiceUrl(url) {
    if (!url) return false;
    if (url.startsWith('/')) return true;
    try {
      const parsed = new URL(url, window.location.origin);
      return this.getInternalServiceOrigins().has(parsed.origin);
    } catch {
      return false;
    }
  }

  isProxiedStreamUrl(url = this.audio?.src) {
    return !!url && String(url).includes('/api/audio/proxy/stream');
  }

  /**
   * 跨域 CDN（阿里云盘直链、网易/酷我等）没有 CORS，Web Audio 无法分析，
   * 粒子封面会停在静止方格。同源代理后才能 createMediaElementSource。
   */
  toSameOriginProxyUrl(url) {
    if (!url) return url;
    if (this.isProxiedStreamUrl(url) || String(url).startsWith('blob:') || String(url).startsWith('data:')) {
      return url;
    }
    return '/api/audio/proxy/stream?url=' + encodeURIComponent(url);
  }

  clearProxySeekRecoverTimer() {
    if (this.proxySeekRecoverTimer) {
      clearTimeout(this.proxySeekRecoverTimer);
      this.proxySeekRecoverTimer = null;
    }
  }

  scheduleProxySeekResume(reason = 'seek') {
    if (!this.isProxiedStreamUrl() || !this.wasPlayingBeforeUserSeek) {
      return;
    }
    this.clearProxySeekRecoverTimer();
    this.proxySeekRecoverTimer = setTimeout(() => {
      this.proxySeekRecoverTimer = null;
      if (!this.audio?.src || !this.isProxiedStreamUrl()) {
        return;
      }
      const target = Number.isFinite(this.lastUserSeekTarget)
        ? this.lastUserSeekTarget
        : this.audio.currentTime;
      if (Number.isFinite(target) && Math.abs(this.audio.currentTime - target) > 1.5) {
        try {
          this.audio.currentTime = target;
        } catch (_) { /* ignore */ }
      }
      if (this.audio.paused) {
        console.warn(`🎵 [AudioController] 代理流 ${reason} 后仍暂停/缓冲，尝试恢复播放`);
        this.audio.play().catch(() => {});
      }
    }, 450);
  }

  /**
   * 判断是否为阿里云盘 CDN 直链（已带 OSS 签名，不能再附加 token，否则破坏签名导致 403）
   * @param {string} url - URL 字符串
   * @returns {boolean}
   */
  isAliyunDirectUrl(url) {
    if (!url) return false;
    return url.includes('aliyundrive.cloud') || url.includes('alipan') || url.includes('x-oss-signature');
  }

  // 从CDN播放
  async playFromCDN(url, startTime = 0, options = {}) {
    console.log('🎵 [AudioController] 从CDN播放:', url);
    const mimeTypeHint = options?.mimeType || this.currentAudioMimeType || null;
    
    try {
      // 🔥 关键修复：在 URL 中添加 token 作为查询参数
      // 因为 Audio 元素不支持设置自定义请求头，所以需要将 token 添加到 URL 中
      const token = tokenService.getAccessToken();
      let finalUrl = url;
      
      // 🎯 判断是否为外部音乐服务 URL（如酷我、QQ音乐等）
      const isExternalMusicUrl = this.isExternalMusicUrl(url);
      // 🎯 判断是否为阿里云盘 CDN 直链（带 OSS 签名）
      const isAliyunDirectUrl = this.isAliyunDirectUrl(url);
      console.log('🎵 [AudioController] URL类型判断:',
        isExternalMusicUrl ? '外部音乐服务' : (isAliyunDirectUrl ? '阿里云盘直链' : '本地/内部服务'));
      
      // 阿里云盘签名音频由用户设备直接读取，避免把所有播放流量绕经本站后端。
      // 直链没有 Web Audio CORS 时，粒子封面使用轻微模拟律动，不为视觉效果消耗服务器资源。
      if (isAliyunDirectUrl) {
        finalUrl = url;
        this.currentAliyunDirectUrl = url;
        this.aliyunRecoverAttempts = 0;
        this.aliyunSongLoadedAt = Date.now();
        this.lastProxyStreamUrl = null;
        console.log('🎵 [AudioController] 阿里云盘音频使用浏览器直连，不经过后端代理');
      } else if (isExternalMusicUrl) {
        const proxyUrl = this.toSameOriginProxyUrl(url);
        finalUrl = proxyUrl;
        this.lastProxyStreamUrl = proxyUrl;
        console.log('🎵 [AudioController] 外部音乐服务，使用后端代理:', finalUrl);
      } else if (token && this.isSameOriginServiceUrl(url)) {
        // 只有本站服务才需要添加认证 token
        const separator = url.includes('?') ? '&' : '?';
        finalUrl = `${url}${separator}token=${encodeURIComponent(token)}`;
        console.log('🎵 [AudioController] 这是内部服务，添加 token 认证');
      } else if (token && (url.startsWith('http://') || url.startsWith('https://'))) {
        finalUrl = url;
        console.log('🎵 [AudioController] 外部 CDN 直链，不附加 token');
      } else {
        console.log('🎵 [AudioController] 无 token，直接使用原始 URL');
      }
      
      console.log('🎵 [AudioController] 最终音频 URL:', finalUrl);
      
      // 验证 URL 格式
      if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://') && !finalUrl.startsWith('/')) {
        console.error('🚫 [AudioController] 错误的 URL 格式:', finalUrl);
        throw new Error(`无效的音频 URL: ${finalUrl}`);
      }
      
      if (!isAliyunDirectUrl) {
        this.currentAliyunDirectUrl = null;
      }
      
      // 设置音频源
      this.ensureUntappedAudioElementForUrl(finalUrl);
      if (isAliyunDirectUrl) {
        // 不设置 crossOrigin：媒体标签可以跨域播放，但无 CORS 的资源不能接入 Web Audio。
        // 去掉 Referer 可避免阿里云 OSS 防盗链误判。
        this.audio.removeAttribute('crossorigin');
        this.audio.setAttribute('referrerpolicy', 'no-referrer');
        // M4A：metadata 模式下 iOS 到 canplay 极慢；改 auto 边下边缓冲
        this.audio.preload = this.isSlowStartAliyunMime(mimeTypeHint) ? 'auto' : 'metadata';
      } else {
        this.audio.crossOrigin = 'anonymous';
        this.audio.removeAttribute('referrerpolicy');
        this.audio.preload = 'metadata';
      }
      this.applyAudioSource(finalUrl, isAliyunDirectUrl ? mimeTypeHint : null);
      this.currentMode = PLAYBACK_MODES.STREAMING;
      const loadGeneration = this.loadGeneration;
      
      // 等待加载完成
      await new Promise((resolve, reject) => {
        const cleanup = () => {
          this.audio.onloadedmetadata = null;
          this.audio.onerror = null;
        };

        this.audio.onloadedmetadata = () => {
          if (this.loadGeneration !== loadGeneration) {
            cleanup();
            reject(Object.assign(new Error('LOAD_SUPERSEDED'), { code: 'LOAD_SUPERSEDED' }));
            return;
          }
          this.audio.currentTime = startTime;
          cleanup();
          resolve();
        };
        this.audio.onerror = (event) => {
          if (this.loadGeneration !== loadGeneration || !this.audio?.src) {
            cleanup();
            reject(Object.assign(new Error('LOAD_SUPERSEDED'), { code: 'LOAD_SUPERSEDED' }));
            return;
          }
          cleanup();
          reject(event);
        };
      });

      if (isAliyunDirectUrl) {
        this.scheduleAliyunCanPlayNudge(loadGeneration);
      }
      
      // 🔥 关键修复：启动智能缓存监听
      // 在音频元素准备好后，立即启动 SmartCacheManager 的缓存监听
      this.startSmartCacheMonitoring();
      
      console.log('🎵 [AudioController] CDN播放准备完成');
      return true;
      
    } catch (error) {
      console.error('🎵 [AudioController] CDN播放失败:', error);
      throw error;
    }
  }

  /**
   * 🎯 启动智能缓存监听
   */
  startSmartCacheMonitoring() {
    if (!this.audio || !this.currentSongId) {
      console.warn('⚠️ [AudioController] 无法启动智能缓存监听：音频元素或歌曲ID无效');
      return;
    }

    try {
      // 获取当前歌曲信息
      const songInfo = {
        title: this.currentSong?.title || '未知歌曲',
        artist: this.currentSong?.artist || '未知艺术家',
        duration: this.audio.duration || 0,
        audio_storage_type: this.currentSong?.audio_storage_type ?? this.currentSong?.audioStorageType ?? this.currentSong?.storageType,
        // 🎬 标识是否为视频版歌曲（音频版歌曲应该为 false）
        isVideoSong: false
      };

      // 启动智能缓存监听
      smartCacheManager.monitorAudioLoading(
        this.audio, 
        this.currentSongId, 
        songInfo
      );

      console.log('🎯 [AudioController] 智能缓存监听已启动:', this.currentSongId);

    } catch (error) {
      console.error('❌ [AudioController] 启动智能缓存监听失败:', error);
    }
  }

  /**
   * 🎯 停止智能缓存监听
   */
  stopSmartCacheMonitoring() {
    if (this.currentSongId) {
      const userId = tokenService.getCurrentUserId();
      if (userId) {
        const cacheKey = smartCacheManager.generateCacheKey(userId, this.currentSongId, 'audio');
        smartCacheManager.cleanupAudioMonitoring(cacheKey);
        console.log('🛑 [AudioController] 智能缓存监听已停止:', this.currentSongId);
      }
    }
  }

  // 播放
  async play() {
    if (this.audio) {
      try {
        await this.audio.play();
        console.log('🎵 [AudioController] 播放开始');
        return true;
      } catch (error) {
        console.error('🎵 [AudioController] 播放失败:', error);
        throw error;
      }
    }
    return false;
  }

  // 暂停
  pause() {
    if (this.audio) {
      this.audio.pause();
      console.log('🎵 [AudioController] 播放暂停');
    }
  }

  // 停止
  stop() {
    if (this.audio) {
      this.audio.pause();
      this.audio.currentTime = 0;
      console.log('🎵 [AudioController] 播放停止');
    }
  }

  // 设置播放时间（userInitiated=false 用于单曲循环等程序性 seek）
  setCurrentTime(time, { userInitiated = true } = {}) {
    if (this.audio) {
      if (userInitiated) {
        this.wasPlayingBeforeUserSeek = !this.audio.paused;
        this.aliyunUserSeekActive = true;
        this.aliyunSeekGraceUntil = Date.now() + 6000;
        this.lastUserSeekTarget = time;
        this.lastUserSeekAt = Date.now();
        this.seekEndedRecoverAttempts = 0;
        this.clearProxySeekRecoverTimer();
      }
      this.audio.currentTime = time;
      if (userInitiated && this.isProxiedStreamUrl()) {
        this.scheduleProxySeekResume('setCurrentTime');
      }
    }
  }

  handleSeeked() {
    const isProxy = this.isProxiedStreamUrl();
    if (!isProxy) {
      this.aliyunUserSeekActive = false;
      this.aliyunSeekGraceUntil = 0;
    }
    if (this.aliyunSeekErrorTimer) {
      clearTimeout(this.aliyunSeekErrorTimer);
      this.aliyunSeekErrorTimer = null;
    }
    this.aliyunRecoverAttempts = 0;
    if (isProxy) {
      this.scheduleProxySeekResume('seeked');
      setTimeout(() => {
        if (this.audio && !this.audio.error) {
          this.aliyunUserSeekActive = false;
          this.aliyunSeekGraceUntil = 0;
        }
      }, 1500);
    }
  }

  // 获取播放时间
  getCurrentTime() {
    return this.audio ? this.audio.currentTime : 0;
  }

  // 获取总时长
  getDuration() {
    return this.audio ? this.audio.duration : 0;
  }

  // 获取播放状态
  isPlaying() {
    return this.audio ? !this.audio.paused : false;
  }

  // 设置音量
  setVolume(volume) {
    if (this.audio) {
      this.audio.volume = Math.max(0, Math.min(1, volume));
    }
  }

  // 获取音量
  getVolume() {
    return this.audio ? this.audio.volume : 1;
  }

  // 停止高频率时间更新
  stopHighFreqTimeUpdate() {
    if (this.highFreqTimeUpdateInterval) {
      clearInterval(this.highFreqTimeUpdateInterval);
      this.highFreqTimeUpdateInterval = null;
      console.log('🧹 [AudioController] 已清除高频率时间更新定时器');
    }
    this.isHighFreqTimeUpdateActive = false;
    this.debugCounter = 0; // 重置调试计数器
    console.log('🧹 [AudioController] 高频率时间更新已完全停止');
  }

  // 清理资源
  cleanup(options = {}) {
    const { preserveAudioElement = true } = options;
    console.log('🧹 [AudioController] 开始清理资源');
    
    // 🔥 首先移除所有事件监听器，避免在清理过程中触发事件
    if (this.audio) {
      this.removeAudioEventListeners();
      console.log('🧹 [AudioController] 已移除所有事件监听器');
    }
    
    // 清理定时器
    if (this.streamingTimeout) {
      clearTimeout(this.streamingTimeout);
      this.streamingTimeout = null;
    }
    
    if (this.switchCancelTimeout) {
      clearTimeout(this.switchCancelTimeout);
      this.switchCancelTimeout = null;
    }
    
    if (this.bufferMonitorInterval) {
      clearInterval(this.bufferMonitorInterval);
      this.bufferMonitorInterval = null;
    }
    
    if (this.cacheDelayTimer) {
      clearTimeout(this.cacheDelayTimer);
      this.cacheDelayTimer = null;
    }
    
    if (this.bufferCheckInterval) {
      clearInterval(this.bufferCheckInterval);
      this.bufferCheckInterval = null;
    }
    
    // 🔥 关键修复：清理高频率时间更新
    if (this.highFreqTimeUpdateInterval) {
      clearInterval(this.highFreqTimeUpdateInterval);
      this.highFreqTimeUpdateInterval = null;
      console.log('🧹 [AudioController] 已清除高频率时间更新定时器');
    }
    this.isHighFreqTimeUpdateActive = false;
    this.debugCounter = 0;
    
    // 清理或重置音频元素
    if (this.audio) {
      try {
        this.audio.pause();
        this.audio.currentTime = 0;
        this.audio.src = '';
        if (preserveAudioElement) {
          this.audio.load();
          console.log('🧹 [AudioController] 已重置音频元素，保留供下一首复用');
        } else {
          if (sharedAudioElement === this.audio) {
            sharedAudioElement = null;
          }
          this.audio = null;
          console.log('🧹 [AudioController] 已释放音频元素');
        }
      } catch (error) {
        console.warn('🧹 [AudioController] 清理音频元素时出错:', error);
        if (!preserveAudioElement) {
          this.audio = null;
        }
      }
    }
    
    // 清理Blob URL
    if (this.currentBlobUrl) {
      try {
        URL.revokeObjectURL(this.currentBlobUrl);
        this.currentBlobUrl = null;
        console.log('🧹 [AudioController] 已清理Blob URL');
      } catch (error) {
        console.warn('🧹 [AudioController] 清理Blob URL时出错:', error);
      }
    }
    
    // 🔥 停止智能缓存监听
    this.stopSmartCacheMonitoring();
    
    // 重置状态
    this.currentMode = null;
    this.currentSongUrl = null;
    this.currentSongId = null;
    this.currentSong = null; // 🔥 清理当前歌曲信息
    this.currentAliyunDirectUrl = null;
    this.aliyunRecoverAttempts = 0;
    this.isSwitching = false;
    this.shouldCacheAfterDelay = false;
    
    console.log('✅ [AudioController] 资源清理完成');
  }

  // 事件处理方法
  handleTimeUpdate() {
    // 🔥 防止在清理过程中访问 null 的 audio 元素
    if (!this.audio) {
      return;
    }
    
    this.emit('timeupdate', {
      currentTime: this.audio.currentTime,
      duration: this.audio.duration
    });
  }

  handleEnded() {
    // 用户拖动进度条 seek 到未缓冲区间时，阿里云盘 Range 流可能瞬间落到文件末尾，
    // 浏览器据此误触发 ended。若刚发生过用户 seek 且目标并不接近结尾，则判定为
    // “seek 误触发的 ended”，重新定位回目标时间继续播放，绝不上抛 ended（否则单曲循环会从头重播）。
    const dur = this.audio?.duration || 0;
    const sinceSeek = Date.now() - (this.lastUserSeekAt || 0);
    const target = this.lastUserSeekTarget;
    const isSeekInducedEnd =
      sinceSeek < 5000 &&
      Number.isFinite(target) &&
      dur > 0 &&
      target < dur - 5;

    if (isSeekInducedEnd) {
      // 这是 seek 误触发的 ended（落到文件末尾），绝不上抛 ended，
      // 否则单曲循环会把歌曲从 0:00 重播（用户明确反感这一行为）。
      const attempts = (this.seekEndedRecoverAttempts || 0) + 1;
      this.seekEndedRecoverAttempts = attempts;
      if (attempts <= 3) {
        console.warn(`🎵 [AudioController] seek 误触发 ended，第 ${attempts} 次重新定位到目标时间:`, target);
        try {
          this.audio.currentTime = target;
          this.audio.play().catch(() => {});
        } catch (_) { /* ignore */ }
      } else {
        // 多次重定位仍失败：保持暂停在当前位置，不重播、不上抛 ended
        console.warn('🎵 [AudioController] seek 重定位多次失败，保持当前位置，不重播');
        try { this.audio.pause(); } catch (_) { /* ignore */ }
      }
      return;
    }

    this.seekEndedRecoverAttempts = 0;
    console.log('🎵 [AudioController] 播放结束');
    this.emit('ended');
  }

  handlePlay() {
    console.log('🎵 [AudioController] 播放事件');
    this.emit('play');
  }

  handlePause() {
    console.log('🎵 [AudioController] 暂停事件');
    this.emit('pause');
  }

  handleError(event) {
    const errorCode = this.audio?.error?.code;
    const errorGeneration = this.loadGeneration;
    console.error('🎵 [AudioController] 音频错误:', event, errorCode != null ? `code=${errorCode}` : '');

    // 切歌清空 src / 中止旧请求时的 code=4
    if (errorCode === 4) {
      console.warn('🎵 [AudioController] 忽略切歌/加载中的 transient 错误');
      return;
    }

    const isProxyStream = this.audio?.src?.includes('/api/audio/proxy/stream');
    const inSeekWindow = this.aliyunUserSeekActive || Date.now() < this.aliyunSeekGraceUntil;

    // 代理流拖动进度时 Range 请求可能短暂失败（code=2），尝试恢复 seek 而不是报错
    if (isProxyStream && errorCode === 2 && inSeekWindow) {
      const target = Number.isFinite(this.lastUserSeekTarget)
        ? this.lastUserSeekTarget
        : this.audio?.currentTime;
      const wasPlaying = this.wasPlayingBeforeUserSeek;
      console.warn('🎵 [AudioController] 代理流 seek 网络错误，尝试恢复位置:', target);
      setTimeout(() => {
        if (this.loadGeneration !== errorGeneration || !this.audio?.src) {
          return;
        }
        const resume = () => {
          if (wasPlaying && this.audio.paused) {
            this.audio.play().catch(() => {});
          }
        };
        try {
          this.audio.currentTime = target;
          resume();
        } catch (_) {
          const src = this.audio.src;
          if (src) {
            this.audio.src = src;
            this.audio.load();
            this.audio.currentTime = target;
            resume();
          }
        }
      }, 300);
      this.scheduleProxySeekResume('network-error');
      return;
    }

    // 非阿里云盘的外部流：其余 transient 错误直接忽略
    if (!this.currentAliyunDirectUrl) {
      console.warn('🎵 [AudioController] 忽略切歌/加载中的 transient 错误');
      return;
    }

    if (this.currentAliyunDirectUrl && this.currentSongId) {
      const errorGeneration = this.loadGeneration;
      const runRecovery = () => {
        if (this.loadGeneration !== errorGeneration) {
          console.warn('🎵 [AudioController] 忽略过期错误的恢复（已切歌）');
          return;
        }
        this.tryRecoverAliyunPlayback(errorGeneration).then((recovered) => {
          if (!recovered && this.loadGeneration === errorGeneration) {
            this.emit('error', { error: '音频播放错误' });
          }
        });
      };

      const inSeekWindow = this.aliyunUserSeekActive || Date.now() < this.aliyunSeekGraceUntil;

      if (inSeekWindow) {
        if (this.aliyunSeekErrorTimer) {
          clearTimeout(this.aliyunSeekErrorTimer);
        }
        this.aliyunSeekErrorTimer = setTimeout(() => {
          this.aliyunSeekErrorTimer = null;
          if (this.audio?.error && this.aliyunUserSeekActive) {
            console.warn('🎵 [AudioController] seek 宽限期后仍有错误，尝试恢复');
            runRecovery();
          }
        }, 2000);
        return;
      }

      // 正常播放续传 transient error：先轻量 nudge，仍失败再换链
      console.warn('🎵 [AudioController] 播放续传 transient error，尝试轻量恢复');
      const savedTime = this.audio.currentTime;
      const wasPlaying = !this.audio.paused;

      if (this.aliyunBufferErrorTimer) {
        clearTimeout(this.aliyunBufferErrorTimer);
      }
      this.aliyunBufferErrorTimer = setTimeout(() => {
        this.aliyunBufferErrorTimer = null;
        if (this.loadGeneration !== errorGeneration) return;
        if (!this.audio || this.aliyunUserSeekActive) return;
        if (!this.audio.error && !this.audio.paused) return;
        const target = Number.isFinite(this.audio.currentTime) ? this.audio.currentTime : savedTime;
        this.audio.currentTime = target;
        if (wasPlaying) {
          this.audio.play().catch(() => {});
        }
      }, 150);

      if (this.aliyunBufferRecoverTimer) {
        clearTimeout(this.aliyunBufferRecoverTimer);
      }
      const msSinceLoad = Date.now() - (this.aliyunSongLoadedAt || 0);
      const recoverDelay = msSinceLoad < 15000 ? 4500 : 2000;
      this.aliyunBufferRecoverTimer = setTimeout(() => {
        this.aliyunBufferRecoverTimer = null;
        if (this.loadGeneration !== errorGeneration) return;
        if (this.audio?.error && !this.aliyunUserSeekActive && this.currentAliyunDirectUrl) {
          console.warn('🎵 [AudioController] 续传轻量恢复失败，尝试换链');
          runRecovery();
        }
      }, recoverDelay);
      return;
    }

    this.emit('error', { error: '音频播放错误' });
  }

  /**
   * 阿里云盘 Range/直链 403 时刷新签名 URL 并恢复播放位置（常见于拖动进度条）
   */
  async tryRecoverAliyunPlayback(expectedGeneration = this.loadGeneration) {
    if (this.loadGeneration !== expectedGeneration) {
      return false;
    }
    if (this.aliyunRecoverInProgress) {
      return true;
    }
    if (this.aliyunRecoverAttempts >= 2) {
      console.warn('🎵 [AudioController] 阿里云盘恢复次数已达上限');
      return false;
    }

    this.aliyunRecoverInProgress = true;
    this.aliyunRecoverAttempts += 1;

    try {
      const seekTime = this.audio?.currentTime || 0;
      const wasPlaying = this.audio && !this.audio.paused;

      console.log('🎵 [AudioController] 阿里云盘播放异常，刷新直链并重试, seek:', seekTime);

      const seekTimeSaved = seekTime;
      const wasPlayingSaved = wasPlaying;

      // 第一次仅重新 seek，不 pause，避免 UI 显示已暂停但无声
      if (this.aliyunRecoverAttempts === 1) {
        this.audio.currentTime = seekTimeSaved;
        if (wasPlayingSaved) {
          try {
            await this.audio.play();
          } catch (_) { /* ignore */ }
        }
        await new Promise((r) => setTimeout(r, 800));
        if (this.loadGeneration !== expectedGeneration) return false;
        if (!this.audio.error) {
          console.log('🎵 [AudioController] 轻量 seek 重试成功');
          return true;
        }
      }

      if (this.loadGeneration !== expectedGeneration) return false;

      const freshInfo = await this.getAliyunAudioUrl(this.currentSongId, true);
      if (!freshInfo?.url || this.loadGeneration !== expectedGeneration) {
        return false;
      }

      this.currentAliyunDirectUrl = freshInfo.url;
      this.currentAudioMimeType = freshInfo.mimeType || this.currentAudioMimeType;
      this.aliyunSongLoadedAt = Date.now();
      const playUrl = freshInfo.url;
      this.lastProxyStreamUrl = null;
      this.ensureUntappedAudioElementForUrl(playUrl);
      this.audio.removeAttribute('crossorigin');
      this.audio.setAttribute('referrerpolicy', 'no-referrer');

      await new Promise((resolve, reject) => {
        const onMeta = () => resolve();
        const onErr = () => reject(new Error('reload failed'));
        this.audio.addEventListener('loadedmetadata', onMeta, { once: true });
        this.audio.addEventListener('error', onErr, { once: true });
        this.applyAudioSource(playUrl, freshInfo.mimeType || this.currentAudioMimeType);
      });

      if (this.loadGeneration !== expectedGeneration) return false;

      this.audio.currentTime = seekTimeSaved;
      if (wasPlayingSaved) {
        await this.audio.play();
      }

      console.log('🎵 [AudioController] 阿里云盘直链已刷新，播放已恢复');
      return true;
    } catch (error) {
      console.warn('🎵 [AudioController] 阿里云盘恢复播放失败:', error);
      return false;
    } finally {
      this.aliyunRecoverInProgress = false;
    }
  }

  handleLoadedMetadata() {
    console.log('🎵 [AudioController] 元数据加载完成');
    this.emit('loadedmetadata', {
      duration: this.audio.duration
    });
    if (this.currentAliyunDirectUrl) {
      this.scheduleAliyunCanPlayNudge(this.loadGeneration);
    }
  }

  handleCanPlay() {
    this._aliyunCanPlayEmittedForGeneration = this.loadGeneration;
    this.clearAliyunCanPlayNudge();
    console.log('🎵 [AudioController] 可以播放');
    this.emit('canplay');
  }

  handleProgress() {
    this.emit('progress');
  }

  handleWaiting() {
    console.log('🎵 [AudioController] 等待数据');
    const inSeekWindow = this.aliyunUserSeekActive || Date.now() < this.aliyunSeekGraceUntil;
    if (inSeekWindow && this.isProxiedStreamUrl()) {
      this.scheduleProxySeekResume('waiting');
    }
    this.emit('waiting');
  }

  handlePlaying() {
    this.clearProxySeekRecoverTimer();
    this.wasPlayingBeforeUserSeek = false;
    if (this.isProxiedStreamUrl()) {
      this.aliyunUserSeekActive = false;
      this.aliyunSeekGraceUntil = 0;
    }
    console.log('🎵 [AudioController] 🔥 正在播放事件触发！音频真正开始播放，用户能听到声音');
    console.log('🎵 [AudioController] 当前音频状态:', {
      paused: this.audio?.paused,
      readyState: this.audio?.readyState,
      currentTime: this.audio?.currentTime,
      duration: this.audio?.duration
    });
    this.emit('playing');
    console.log('🎵 [AudioController] playing 事件已发出，等待策略监听器处理');
  }

  handlePageUnload() {
    console.log('🎵 [AudioController] 页面卸载，清理资源');
    this.cleanup({ preserveAudioElement: false });
  }

  // 事件系统
  on(event, callback) {
    if (this.eventCallbacks[event]) {
      this.eventCallbacks[event].push(callback);
    }
  }

  off(event, callback) {
    if (this.eventCallbacks[event]) {
      const index = this.eventCallbacks[event].indexOf(callback);
      if (index > -1) {
        this.eventCallbacks[event].splice(index, 1);
      }
    }
  }

  emit(event, data) {
    if (this.eventCallbacks[event]) {
      this.eventCallbacks[event].forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('🎵 [AudioController] 事件回调错误:', error);
        }
      });
    }
  }

  // 查找现有缓存
  async findExistingCache(userId, songId) {
    try {
      if (!userId || !songId) {
        console.warn('🎵💾 [AudioController] 查找缓存：用户ID或歌曲ID为空', { userId, songId });
        return null;
      }
      
      // 🔥 关键修复：使用正确的缓存键格式 {userId}_{songId}_audio
      // 格式必须与 SmartCacheManager 保持一致：22_2959_audio
      const cacheKey = `${userId}_${songId}_audio`;
      console.log('🎵💾 [AudioController] 生成缓存键（格式: userId_songId_audio):', cacheKey);
      console.log('🎵💾 [AudioController] 正在从 IndexedDB 检查缓存是否存在...');
      
      const exists = await musicCache.get(cacheKey);
      
      if (exists) {
        console.log('🎵✅ [AudioController] 缓存存在，缓存大小:', exists.byteLength || exists.length, '字节');
        return cacheKey;
      } else {
        console.log('🎵💾 [AudioController] 缓存不存在');
        
        // 🔄 兼容性检查：也尝试查找旧格式的缓存（user_{userId}_song_{songId}_*）
        console.log('🎵💾 [AudioController] 尝试查找旧格式缓存...');
        try {
          const allKeys = await musicCache.keys();
          for (const key of allKeys) {
            // 检查是否是旧格式且属于当前用户和歌曲
            if (isCacheKeyBelongsToUser(key, userId)) {
              const cachedSongId = extractSongIdFromCacheKey(key);
              if (cachedSongId === String(songId)) {
                console.log('🎵✅ [AudioController] 找到旧格式缓存:', key);
                console.log('🎵💾 [AudioController] 建议迁移到新格式:', cacheKey);
                // 返回旧格式的缓存键，但建议后续迁移
                return key;
              }
            }
          }
        } catch (error) {
          console.warn('🎵💾 [AudioController] 查找旧格式缓存时出错:', error);
        }
        
        return null;
      }
    } catch (error) {
      console.error('🎵❌ [AudioController] 查找缓存失败:', error);
      return null;
    }
  }

  // 迁移旧格式缓存
  async migrateOldCaches(userId, songId, url) {
    try {
      // 这里可以实现旧缓存格式的迁移逻辑
      return null;
    } catch (error) {
      console.error('🎵 [AudioController] 迁移旧缓存失败:', error);
      return null;
    }
  }
}

/** 全局复用的播放用 audio 元素，供封面粒子等可视化模块读取 */
export function getSharedPlaybackAudioElement() {
  return sharedAudioElement;
}

export default AudioController;
